import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Alert
} from '@mui/material';
import Modal, { Field } from '../../../../../../common/Modal';
import EmployeeSearch from '../../../../../../common/EmployeeSearch';
import { EmployeeAvatar } from '../../../../../../common/EmployeeAvatar';
import { projectsApi, employeesApi } from '../../../../../../../services/api';
import { useAlert } from '../../../../../../../contexts/AlertContext';
import LoadingSpinner from '../../../../../../common/LoadingSpinner';
import ManagerOfRecordHistory from './ManagerOfRecordHistory';

/**
 * Modal for overriding an employee's manager
 * 
 * @param {Object} props
 * @param {Boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.employee - The employee whose manager is being overridden
 * @param {String} props.projectId - The project ID
 * @param {String} props.businessUnitId - The business unit ID
 * @param {Function} props.onSuccess - Callback when override is successful
 */
const OverrideManagerModal = ({ 
  isOpen, 
  onClose, 
  employee, 
  projectId, 
  businessUnitId, 
  onSuccess 
}) => {
  const { showAlert } = useAlert();
  const [selectedManager, setSelectedManager] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);

  useEffect(() => {
    if (isOpen && employee?.id) {
      setSelectedManager(null);
      fetchEmployeeData();
    }
  }, [isOpen, employee?.id]);

  const fetchEmployeeData = async () => {
    try {
      setIsLoadingEmployee(true);
      const data = await employeesApi.getEmployeeById(employee.id);
      setEmployeeData(data);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      showAlert(error.message || 'Failed to fetch individual data', 'error');
      setEmployeeData(null);
    } finally {
      setIsLoadingEmployee(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedManager) {
      showAlert('Please select a new representative', 'error');
      return;
    }

    if (selectedManager.id === employeeData?.directManager?.id) {
      showAlert('Please select a different representative', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const data = {
        managerId: selectedManager.id,
        businessUnitId,
        projectId
      };

      await projectsApi.overrideEmployeeManager(employee.id, data);
      
      showAlert('Representative override successful', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error overriding representative:', error);
      showAlert(error.message || 'Failed to override representative', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!employee) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Override Representative"
      maxWidth="md"
      fullWidth
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Overriding...' : 'Override Representative'}
      submitDisabled={!selectedManager || isSubmitting}
    >
      {isLoadingEmployee ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Employee Info */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Employee
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <EmployeeAvatar employee={employeeData || employee} size={40} />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {employeeData?.fullName || employee.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {employeeData?.position?.label || employee.position?.label || 'No position assigned'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Current Manager */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Current Representative
            </Typography>
            {employeeData?.directManager ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <EmployeeAvatar employee={employeeData.directManager} size={40} />
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {employeeData.directManager.fullName}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">No current representative assigned</Alert>
            )}
          </Box>

          {/* New Manager Selection */}
          <Field>
            <Typography variant="subtitle1" gutterBottom>
              Select New Representative
            </Typography>
            <EmployeeSearch
              value={selectedManager}
              onChange={setSelectedManager}
              label=""
              placeholder="Search for new representative..."
              hideLabel
              width={9999}
              disabledEmployeeIds={employeeData?.directManager ? [employeeData.directManager.id, employee.id] : [employee.id]}
              showSelectedAvatar
              sx={{ width: '100% !important' }}
            />
          </Field>

          {/* Manager History */}
          <ManagerOfRecordHistory employeeId={employee.id} />
        </Box>
      )}
    </Modal>
  );
};

export default OverrideManagerModal;
